import { useState, useRef, useEffect, useCallback } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Col, Row, Form, Modal, Tooltip, OverlayTrigger, Alert } from "react-bootstrap";
import { useTranslation } from "react-i18next";

import { AgGridReact } from "ag-grid-react";
import "ag-grid-enterprise";

import { actionTypeSelector, importEmployeesSelector, personsSelector } from "../reducers";
import { getLocaleText } from "../actions/datagrid";
import { defaultColDef, contextMenu, sideBar, statusBar, statusBarSelected, multiRow } from "./grid";
import Loading from "./loaders/loading";
import { CheckCircleIcon, CrossCircleIcon } from "./icons";
import { exportSevera } from "../actions/persons/importemployees";
import { getAllPersons } from "../actions/persons/personsgetall";
import { Button } from "./Button.tsx";
import Switch from "./Switch";

const initialData = { updateExisting: false };

export function SeveraExportModal({ hideModal }) {
  const { t } = useTranslation();

  const dispatch = useDispatch();
  const persons = useSelector(personsSelector);
  const actionType = useSelector(actionTypeSelector);
  const importEmployeesResult = useSelector(importEmployeesSelector);

  const [selectedRows, setSelectedRows] = useState([]);
  const [data, setData] = useState(initialData);
  const [showResults, setShowResults] = useState(false);
  const [showResultLoading, setShowResultLoading] = useState(false);
  const [tableData, setTableData] = useState([]);

  useEffect(() => {
    if (!persons || persons.length === 0) {
      dispatch(getAllPersons()); // get active persons
    }

    return () => dispatch({ type: "PERSONS_UNMOUNT", payload: { persons: null } });
  }, []);

  useEffect(() => {
    if (actionType === "IMPORT_EMPLOYEES_SUCCEEDED") {
      setShowResultLoading(false);
      setTableData(importEmployeesResult.employees);
      dispatch(getAllPersons()); // update persons list to show persons in Severa
    } else if (actionType === "IMPORT_EMPLOYEES_FAIL") {
      setShowResultLoading(false);
    }
  }, [actionType]);

  const dataChange = ({ target }) => {
    const newData = { ...data };
    let value = target.type === "checkbox" ? target.checked : target.value;

    newData[target.id] = value;
    setData(newData);
  };

  const handleExport = () => {
    setTableData([]);
    dispatch({
      type: "EXPORT_EMPLOYEES_RESET",
      payload: { importEmployees: null },
    });

    const personIds = selectedRows.map(({ personId }) => personId);

    dispatch(exportSevera(data.updateExisting, personIds));
    setShowResultLoading(true);
    setShowResults(true);
  };

  const gridRef = useRef();
  const getRowId = (params) => String(params.data.nr);

  const importStatusValueFormatter = (params) => {
    if (params.value === true || params.value === "true") {
      return t("ImportStatus_SUCCESS", "Onnistui");
    } else if (params.value === false || params.value === "false") {
      return t("ImportStatus_FAIL", "Epäonnistui");
    }
  };

  const importActionValueFormatter = ({ value }) => (value === "CREATE" ? t("Create", "Luo") : t("Update", "Päivitä"));

  const importErrorValueFormatter = (params) => {
    if (params && params.data && params.data.errorMessages) {
      return params.data.errorMessages.map(handleErrorMessages);
    }
  };

  const handleErrorMessages = (value) => {
    const key = value.key;
    const defaultValue = value.defaultValue;
    if (key === "NOT_LOCALIZED_UNEXPECTED_ERROR") {
      return defaultValue;
    } else {
      return t(key, defaultValue);
    }
  };

  const ImportStatusRenderer = (params) => {
    let statusIcon;
    if (params.value === true) {
      statusIcon = <CheckCircleIcon />;
    } else if (params.value === false) {
      statusIcon = <CrossCircleIcon />;
    }

    const statusTooltip = <Tooltip id="tooltip-right">{importStatusValueFormatter(params)}</Tooltip>;
    return (
      <>
        <OverlayTrigger key="right" placement="right" overlay={statusTooltip}>
          <span>{statusIcon}</span>
        </OverlayTrigger>
      </>
    );
  };

  const gridComponents = {
    importStatusRenderer: ImportStatusRenderer,
  };

  const columnDefs = [
    {
      headerName: t("Status", "Tila"),
      field: "success",
      valueFormatter: importStatusValueFormatter,
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: importStatusValueFormatter,
      },
      cellRenderer: "importStatusRenderer",
    },
    {
      headerName: t("Number", "Numero"),
      field: "nr",
      filter: "agNumberColumnFilter",
    },
    {
      headerName: t("Name", "Nimi"),
      field: "name",
    },
    {
      headerName: t("Action", "Toiminto"),
      field: "action",
      valueFormatter: importActionValueFormatter,
      filter: "agSetColumnFilter",
      filterParams: {
        valueFormatter: importActionValueFormatter,
      },
    },
    {
      headerName: t("Errors", "Virheet"),
      field: "errorMessages",
      valueGetter: importErrorValueFormatter,
      tooltipValueGetter: importErrorValueFormatter,
      filter: "agSetColumnFilter",
    },
  ];

  const personColDef = [
    {
      field: "nr",
      headerName: t("NumberAbbr", "Nro"),
      filter: "agNumberColumnFilter",
      //headerCheckboxSelection: true,
      //checkboxSelection: true,
    },
    {
      headerName: t("Name", "Nimi"),
      field: "name",
    },
    {
      headerName: t("JobTitle", "Tehtävänimike"),
      field: "employmentDetails.profession",
    },
    {
      headerName: t("OnSevera", "Severassa"),
      field: "settings.severaGuid",
      cellDataType: "boolean",
      filter: "agSetColumnFilter",
      valueGetter: ({ data }) => !!data.settings?.severaGuid,
    },
  ];

  const getContextMenuItems = useCallback((params) => {
    return contextMenu(t, params);
  }, []);

  return (
    <Modal show onHide={hideModal} backdrop="static" size="xl" fullscreen="lg-down">
      <Modal.Header closeButton>
        <Modal.Title>{t("SeveraExportDesc", "Vie henkilöt Visma Severaan")}</Modal.Title>
      </Modal.Header>

      <Modal.Body>
        <Row className="mb-1">
          <Form.Group as={Col} xl={12} controlId="updateExisting">
            <Switch
              className="mb-1"
              label={t("UpdateExisting", "Päivitä olemassa olevat")}
              onChange={dataChange}
              checked={data.updateExisting}
            />
          </Form.Group>
        </Row>

        <Row className="styles-ag flex-grow-1 ag-theme-material" style={{ height: "400px" }}>
          <Col>{t("SelectPersons", "Valitse henkilöt")}</Col>
          <AgGridReact
            columnDefs={personColDef}
            defaultColDef={{ ...defaultColDef, enableCellChangeFlash: true }}
            sideBar={sideBar}
            rowData={persons}
            getContextMenuItems={getContextMenuItems}
            statusBar={statusBarSelected}
            getRowId={getRowId}
            getLocaleText={(params) => getLocaleText(t, params)}
            rowSelection={multiRow}
            onSelectionChanged={({ api }) => setSelectedRows(api.getSelectedRows())}
            animateRows
            suppressCellFocus
            debug={import.meta.env.DEV}
          />
        </Row>

        {showResults && (
          <>
            <hr className="mb-12" />

            <Row className="mb-1">
              <Row className="mb-1">
                <Col sm="12">
                  <h3>{t("Results", "Tulokset")}</h3>
                </Col>
              </Row>

              {showResultLoading && (
                <div className="d-flex justify-content-center">
                  <Loading show={showResultLoading} />
                </div>
              )}

              {importEmployeesResult && (
                <>
                  <Row className="mb-1">
                    <Col sm="12">
                      <Alert key="info" variant="info">
                        <Row className="mb-1">
                          <Col sm="3">{t("PersonsExported", "Henkilöitä vietiin")}:</Col>
                          <Col sm="1">
                            <strong>{importEmployeesResult.created}</strong>
                          </Col>
                          <Col sm="1"></Col>
                          <Col sm="3">{t("PersonsExportsFailed", "Henkilön vientiä epäonnistui")}:</Col>
                          <Col sm="1">
                            <strong style={{ color: "red" }}>{importEmployeesResult.createFailed}</strong>
                          </Col>
                          <Col sm="1"></Col>
                          <Col sm="1">{t("Took", "Kesto")}:</Col>
                          <Col sm="1">
                            <strong>{importEmployeesResult.took} ms</strong>
                          </Col>
                        </Row>
                        <Row className="mb-1">
                          <Col sm="3">{t("PersonsUpdated", "Henkilöitä päivitettiin")}:</Col>
                          <Col sm="1">
                            <strong>{importEmployeesResult.updated}</strong>
                          </Col>
                          <Col sm="1"></Col>
                          <Col sm="3">{t("PersonsUpdatesFailed", "Henkilön päivitystä epäonnistui")}:</Col>
                          <Col sm="1">
                            <strong style={{ color: "red" }}>{importEmployeesResult.updateFailed}</strong>
                          </Col>
                        </Row>
                      </Alert>
                    </Col>
                  </Row>

                  <Row className="styles-ag flex-grow-1 ag-theme-material" style={{ height: "400px" }}>
                    <AgGridReact
                      ref={gridRef}
                      components={gridComponents}
                      columnDefs={columnDefs}
                      defaultColDef={defaultColDef}
                      sideBar={sideBar}
                      rowData={tableData}
                      getContextMenuItems={getContextMenuItems}
                      statusBar={statusBar}
                      getRowId={getRowId}
                      getLocaleText={(params) => getLocaleText(t, params)}
                      animateRows
                      suppressCellFocus
                      enableCellTextSelection
                      onFirstDataRendered={(params) => params.api.autoSizeAllColumns()}
                      debug={import.meta.env.DEV}
                    />
                  </Row>
                </>
              )}
            </Row>
          </>
        )}
      </Modal.Body>

      <Modal.Footer>
        <Button variant="secondary" onClick={hideModal}>
          {t("Close", "Sulje")}
        </Button>
        <Button variant="success" onClick={handleExport} disabled={!selectedRows.length}>
          {t("Export", "Vie")}
        </Button>
      </Modal.Footer>
    </Modal>
  );
}
